import Input from '@anm/components/form/Input';
import { defaultCap } from '@anm/data/streaming/destinationsCaps';
import isMobile from '@anm/helpers/is/isMobile';
import noop from '@anm/helpers/noop';
import { useCallback, FC } from 'react';

import {
  ButtonWrapper,
  FieldCounter,
  FolderPathInput,
  InputsWrapper,
  Label,
  ModalTitle
} from '../../../StreamModalWrapper';
import { NewStreamStepProps } from '../../../types';
import useValidateTextField from '../../hooks/useValidateTextField';

import { ButtonsContainer, ButtonsRow, Hint } from './Wrapper';

const RecordingStep: FC<NewStreamStepProps> = ({
  name,
  path,
  isEdit,
  isPending,
  isDuplicate,
  isRecording,
  isDisabledSubmit,
  onSubmit,
  onSelectPath,
  onNameChange,
  onSetupNavigate
}) => {
  const isMobileDevice = isMobile();
  const [isError, validateName, handleNameChange] = useValidateTextField({ text: name, onTextChange: onNameChange });

  const titleMaxLength = defaultCap.titleMaxLength;

  const handleCreateRecording = useCallback(() => {
    const isValidName = validateName({ titleMaxLength });

    isValidName && onSubmit();
  }, [onSubmit, validateName]);

  const title =
    isEdit && !isDuplicate && !isRecording
      ? 'Edit stream'
      : isRecording && !isDuplicate
      ? 'Edit recording'
      : 'New stream';

  const submitText =
    isEdit && !isDuplicate && !isRecording
      ? 'Record instead'
      : isEdit && !isDuplicate
      ? 'Update recording'
      : 'Open studio';

  const isDisabled = isDisabledSubmit && !isDuplicate;

  const leftButtonHint =
    isEdit && !isDuplicate && !isRecording
      ? 'Remove events from destinations and change to recording'
      : isEdit
      ? 'Update title and folder <br />of this recording'
      : 'Open live studio to <br />pre-record my stream';

  const rightButtonHint =
    isEdit && !isDuplicate && isRecording
      ? 'Change to live stream and setup live streaming events on destinations'
      : isEdit && !isDuplicate
      ? 'Edit live streaming events <br/>on destinations'
      : 'Setup live streaming event <br/>on social media platforms';

  const rightButtonText = isEdit && !isDuplicate && !isRecording ? 'Edit live stream' : 'Setup live stream';

  return (
    <>
      <ModalTitle {...{ title }} />
      <InputsWrapper>
        <Label>
          <FieldCounter isMax={name.length >= titleMaxLength}>
            {name.length} / {titleMaxLength}
          </FieldCounter>
          <span>Event title</span>
          <Input variant="simple-small" value={name} onChange={handleNameChange} hasError={isError} />
        </Label>
        <Label>
          <span>Select the folder for recording</span>
          <FolderPathInput variant="simple-small" value={path} onClick={onSelectPath} onChange={noop} />
        </Label>
      </InputsWrapper>
      <ButtonsContainer>
        <ButtonsRow>
          <Hint dangerouslySetInnerHTML={{ __html: leftButtonHint }} />
          <Hint dangerouslySetInnerHTML={{ __html: rightButtonHint }} />
        </ButtonsRow>
        <ButtonsRow>
          <ButtonWrapper
            size="big"
            variant={isMobileDevice ? 'blue-heavy-border' : 'light-blue'}
            isPending={isPending}
            disabled={isDisabled}
            icon={isPending || isDisabled || isMobileDevice ? '' : 'images/icons/recording-white.svg'}
            onClick={handleCreateRecording}
          >
            {submitText}
          </ButtonWrapper>
          <ButtonWrapper
            size="big"
            icon={isMobileDevice ? '' : 'images/icons/broadcast_white.svg'}
            variant="light-blue"
            onClick={onSetupNavigate}
          >
            {rightButtonText}
          </ButtonWrapper>
        </ButtonsRow>
      </ButtonsContainer>
    </>
  );
};

export default RecordingStep;
